import React, { useState } from "react";
import axios from "axios";
import { Formik, Field, Form, ErrorMessage } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "../assets/css/form.css";
import VerifyOtp from "./otp";
import { BASE_URL } from "../config";

const MobileNumberChangeForm = () => {
  const formikRef = React.useRef(null);
  const fileInputRef = React.useRef(null);
  const fileInput2Ref = React.useRef(null);

  const [loading, setLoading] = useState(false);
  const [signaturePreview, setSignaturePreview] = useState(null);
  const [photoPreview, setphotoPreview] = useState(null);

  const [showOtpPopup, setShowVerifyOtp] = useState(false);
  const [sessionId, setSessionId] = useState("");
  const [newMobileNumber, setNewMobileNumber] = useState("");
  const [signatureUrl, setSignatureUrl] = useState("");
  const [photoUrl, setPhotoUrl] = useState(null);

  const [isBtnDisabled, setBtnDisabled] = useState(false);

  const validationSchema = Yup.object({
    customerNumber: Yup.string().required("Customer No is required"),
    customerName: Yup.string().required("Customer Name is required"),
    aadhaar: Yup.string().required("Aadhaar Number is required"),
    oldMobileNumber: Yup.string()
      .matches(/^[0-9]{10}$/, "Old mobile number must be 10 digits")
      .required("Old Mobile Number is required"),
    newMobileNumber: Yup.string()
      .matches(/^[0-9]{10}$/, "New mobile number must be 10 digits")
      .required("New Mobile Number is required"),
    signature: Yup.mixed()

      .test(
        "fileSize",
        "File size is too large",
        (value) => value && value.size <= 5000000
      ) // 5MB max
      .test(
        "fileType",
        "Unsupported file format",
        (value) => value && ["image/jpeg", "image/png"].includes(value.type)
      ), // Allow only JPEG/PNG
    photo: Yup.mixed()
      .required("Profile Image is required")
      .test(
        "fileSize",
        "File size is too large",
        (value) => value && value.size <= 5000000
      ) // 5MB max
      .test(
        "fileType",
        "Unsupported file format",
        (value) => value && ["image/jpeg", "image/png"].includes(value.type)
      ),
  });

  const handleFileUpload = async (file, type) => {
    const formData = new FormData();
    formData.append("image", file);

    try {
      setBtnDisabled(true);
      const response = await axios.post(`${BASE_URL}/file/upload`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (response.status === 200) {
        if (type === "signature") {
          setSignatureUrl(response.data.data); // Set signature URL after successful upload
          toast.success("photo uploaded successfully");
        } else if (type === "photo") {
          setPhotoUrl(response.data.data); // Set signature URL after successful upload
          toast.success("photo uploaded successfully");
        } else {
          toast.success("Photo uploaded successfully");
          // Handle photo URL if needed
        }
      }
    } catch (err) {
      toast.error("Failed to upload signature");
      console.error(err);
    } finally {
      setBtnDisabled(false);
    }
  };

  const handleSubmit = async (values, { resetForm }) => {
    setLoading(true);

    const payload = {
      ...values,
      signature: signatureUrl,
      photo: photoUrl,
    };

    try {
      const response = await axios.post(
        `${BASE_URL}/form/mobilenumberchange`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (response.status === 200) {
        setSessionId(response.data.data.otpSessionId);
        setShowVerifyOtp(true);
        setNewMobileNumber(values.newMobileNumber);
      } else {
        toast.error("Failed to submit the form, please try again.");
      }
    } catch (error) {
      if (error.response.data.status === 409) {
        toast.error(error.response.data.message);
      } else {
        toast.error(
          "An error occurred while submitting the form. Please try again."
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const onSubmitOtp = async (otp) => {
    setLoading(true);

    const payload = {
      otpSessionId: sessionId,
      otp: otp,
      newMobileNumber: newMobileNumber,
    };

    try {
      const response = await axios.post(
        `${BASE_URL}/form/mobilenumberchange/verify`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (response.status === 200) {
        setShowVerifyOtp(false);
        if (formikRef.current) {
          formikRef.current.resetForm();
        }
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
        if (fileInput2Ref.current) {
          fileInput2Ref.current.value = "";
        }
        setSignaturePreview(null);
        setPhotoUrl(null);
        setphotoPreview(null);
        toast.success("Your request has been submitted successfully.");
      } else {
        toast.error("Failed to submit the form, please try again.");
      }
    } catch (err) {
      toast.error("An error occurred while submitting your request.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="form-container" id="numberChange">
      <h2>Application For Mobile Number Change</h2>
      <Formik
        innerRef={formikRef}
        initialValues={{
          customerNumber: "",
          customerName: "",
          aadhaar: "",
          oldMobileNumber: "",
          newMobileNumber: "",
          signature: null,
          photo: "",
        }}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ setFieldValue, values, isSubmitting }) => (
          <Form>
            <div className="form-row">
              {/* First Column */}
              <div className="form-column">
                <div className="form-group">
                  <label>
                    Customer No:<sup>*</sup>
                  </label>
                  <Field
                    type="text"
                    name="customerNumber"
                    className="form-input"
                  />
                  <ErrorMessage
                    name="customerNumber"
                    component="div"
                    className="error-message text-danger"
                  />
                </div>
                <div className="form-group">
                  <label>
                    Customer Name:<sup>*</sup>
                  </label>
                  <Field
                    type="text"
                    name="customerName"
                    className="form-input"
                  />
                  <ErrorMessage
                    name="customerName"
                    component="div"
                    className="error-message text-danger"
                  />
                </div>
                <div className="form-group">
                  <label>
                    Aadhaar Number<sup>*</sup>
                  </label>
                  <Field type="text" name="aadhaar" className="form-input" />
                  <ErrorMessage
                    name="aadhaar"
                    component="div"
                    className="error-message text-danger"
                  />
                </div>
                <div className="form-group">
                  <label>
                    Old Mobile Number:<sup>*</sup>
                  </label>
                  <Field
                    type="text"
                    name="oldMobileNumber"
                    className="form-input"
                  />
                  <ErrorMessage
                    name="oldMobileNumber"
                    component="div"
                    className="error-message text-danger"
                  />
                </div>
                <div className="form-group">
                  <label>
                    New Mobile Number:<sup>*</sup>
                  </label>
                  <Field
                    type="text"
                    name="newMobileNumber"
                    className="form-input"
                  />
                  <ErrorMessage
                    name="newMobileNumber"
                    component="div"
                    className="error-message text-danger"
                  />
                </div>
              </div>

              {/* Second Column */}
              <div className="form-column fw-bold">
                <div className="form-group">
                  <label>TO:</label>
                  <p>The Proprietor</p>
                  <p>Rajeshwari Maposi Indane Agency</p>
                  <p>Tondiarpet, Chennai - 600081</p>
                </div>
                <div className="letter-content">
                  <p>
                    Sir,
                    <br />I am informing you that I have changed my mobile
                    number to a new one.
                  </p>
                  <p>Thanking you.</p>
                  <p>Yours faithfully,</p>
                </div>
                <div className="form-group">
                  <label>Signature:</label>
                  <input
                    type="file"
                    name="signature"
                    ref={fileInputRef}
                    onChange={(e) => {
                      const file = e.target.files[0];
                      setFieldValue("signature", file);
                      if (file) {
                        const reader = new FileReader();
                        reader.onloadend = () => {
                          setSignaturePreview(reader.result); // Show preview
                        };
                        reader.readAsDataURL(file);
                        handleFileUpload(file, "signature");
                      }
                    }}
                    required
                  />
                  <ErrorMessage
                    name="signature"
                    component="div"
                    className="error-message text-danger"
                  />
                </div>
                {signaturePreview && (
                  <div>
                    <img
                      className="signature-preview"
                      src={signaturePreview}
                      alt="Signature Preview"
                    />
                  </div>
                )}
                <div className="form-group">
                  <label>Profile Image:</label>
                  <input
                    type="file"
                    name="photo"
                    ref={fileInput2Ref}
                    onChange={(e) => {
                      const file = e.target.files[0];
                      setFieldValue("photo", file);
                      if (file) {
                        const reader = new FileReader();
                        reader.onloadend = () => {
                          setphotoPreview(reader.result);
                        };
                        reader.readAsDataURL(file);
                        handleFileUpload(file, "photo");
                      }
                    }}
                    required
                  />
                  <ErrorMessage
                    name="photo"
                    component="div"
                    className="error-message text-danger"
                  />
                </div>
                {photoPreview && (
                  <div>
                    <img
                      className="photo-preview"
                      src={photoPreview}
                      alt="Photo Preview"
                    />
                  </div>
                )}
              </div>
            </div>

            <button
              type="submit"
              className="submitBtn"
              disabled={isSubmitting || loading || isBtnDisabled}
            >
              {loading ? "Submitting..." : "Submit"}
            </button>
          </Form>
        )}
      </Formik>

      <VerifyOtp
        modalIsOpen={showOtpPopup}
        closeModal={() => setShowVerifyOtp(false)}
        onConfirmOtp={onSubmitOtp}
      />
    </div>
  );
};

export default MobileNumberChangeForm;
